import { toZonedTime } from 'date-fns-tz';
import { isValidDate } from '../../sharedComponents/dateHelper';
import {
    AddressAndContact,
    ExecutionStatus,
    MeansOfTransport,
    TransportAssignment,
} from '../transportAssignment.types';
import {
    getIsConfirmed,
    getOrderIds,
    hasNewCar,
    hasVehiclesInStatus,
    isBundle,
    isCapacityOrder,
    isTransportOrder,
    mapToVehiclesInExecution,
} from '../TransportAssignmentUtils';
import { DatePickerFormGroup } from './confirmationForm/DatePickerFormGroup';
import { MeansOfTransportFormInput } from './confirmationForm/meansOfTransportInput/MeansOfTransportFormInput';
import { TrailerFormInput } from './confirmationForm/trailerInput/TrailerFormInput';
import { UnloadingAddressFormGroup } from './confirmationForm/UnloadingAddressFormGroup';
import { useTransportAssignmentConfirmationForm } from './confirmationForm/useTransportAssignmentConfirmationForm';
import { ConfirmationButton } from '../shared/ConfirmationButton';
import { IncidentModal } from './details/IncidentModal';

export type ConfirmationFormParams = {
    newConfirmedPickUpDate: Date | undefined;
    newConfirmedDeliveryDate: Date | undefined;
    newMeansOfTransport: MeansOfTransport | undefined;
    newUnloadingAddress: AddressAndContact | undefined;
};

export enum DatePickerType {
    PICKUP_DATE_PICKER,
    DELIVERY_DATE_PICKER,
}

type TransportAssignmentConfirmationFormProps = {
    transportAssignment: TransportAssignment;
};

// eslint-disable-next-line max-lines-per-function
export const TransportAssignmentConfirmationForm = ({
    transportAssignment,
}: TransportAssignmentConfirmationFormProps) => {
    // TODO: Migrate to a form to make the content change and button disabling easier,
    //  since those are provided by the form (disable button when confirming or with invalid content,
    //  or without changes)
    const {
        assignmentData: { requestedPickUpDate, pickUpTimeZone, requestedDeliveryDate, deliveryTimeZone },
        formState: { confirmedPickUpDate, confirmedDeliveryDate, meansOfTransport, unloadingAddress },
        validation,
        setConfirmedPickUpDate,
        setConfirmedDeliveryDate,
        setMeansOfTransport,
        setTrailerId,
        setUnloadingAddress,
        onConfirmOrder,
        isLoading,
        isFormDisabled,
    } = useTransportAssignmentConfirmationForm(transportAssignment);

    const hasSelectableUnloadingAddress =
        isTransportOrder(transportAssignment) && transportAssignment.hasSelectableUnloadingAddress;

    const onConfirmTransportAssignment = () =>
        onConfirmOrder({
            newConfirmedPickUpDate: isValidDate(confirmedPickUpDate) ? confirmedPickUpDate : undefined,
            newConfirmedDeliveryDate: isValidDate(confirmedDeliveryDate) ? confirmedDeliveryDate : undefined,
            newMeansOfTransport: meansOfTransport,
            newUnloadingAddress: unloadingAddress,
        });

    return (
        <>
            <div className="display-flex align-items-start">
                <MeansOfTransportFormInput
                    meansOfTransport={meansOfTransport}
                    setMeansOfTransportId={setMeansOfTransport}
                    validation={validation.meansOfTransport}
                    isFormDisabled={isFormDisabled}
                />
                <TrailerFormInput
                    meansOfTransport={meansOfTransport}
                    setTrailerId={setTrailerId}
                    validation={validation.trailer}
                    isFormDisabled={isFormDisabled}
                />
            </div>
            <div className="display-flex align-items-start">
                <UnloadingAddressFormGroup
                    transportOrderId={getOrderIds(transportAssignment)[0]}
                    unloadingAddress={unloadingAddress}
                    setUnloadingAddress={setUnloadingAddress}
                    isBundle={isBundle(transportAssignment)}
                    isSelectable={hasSelectableUnloadingAddress}
                    validation={validation.unloadingAddress}
                    isFormDisabled={isFormDisabled}
                />
            </div>

            <DatePickerFormGroup
                datePickerType={DatePickerType.PICKUP_DATE_PICKER}
                displayFieldMessageId="outboundPortal.transportAssignments.sidebar.assignmentInformation.requestedPickUpDateInterval"
                inputFieldMessageId="outboundPortal.transportAssignments.sidebar.assignmentInformation.confirmedPickUpDate"
                displayFieldValue={requestedPickUpDate}
                timeZone={pickUpTimeZone}
                inputFieldValue={
                    isValidDate(confirmedPickUpDate)
                        ? toZonedTime(confirmedPickUpDate, pickUpTimeZone)
                        : confirmedPickUpDate
                }
                onChange={setConfirmedPickUpDate}
                validation={validation.confirmedPickUpDate}
                isFormDisabled={isFormDisabled}
            />
            <DatePickerFormGroup
                datePickerType={DatePickerType.DELIVERY_DATE_PICKER}
                displayFieldMessageId="outboundPortal.transportAssignments.sidebar.assignmentInformation.requestedDeliveryDate"
                inputFieldMessageId="outboundPortal.transportAssignments.sidebar.assignmentInformation.confirmedDeliveryDate"
                displayFieldValue={requestedDeliveryDate}
                timeZone={deliveryTimeZone}
                inputFieldValue={
                    isValidDate(confirmedDeliveryDate)
                        ? toZonedTime(confirmedDeliveryDate, deliveryTimeZone)
                        : confirmedDeliveryDate
                }
                onChange={setConfirmedDeliveryDate}
                validation={validation.confirmedDeliveryDate}
                isFormDisabled={isFormDisabled}
            />
            <div className="display-flex justify-content-between align-items-center margin-top-20">
                {!isCapacityOrder(transportAssignment) && !hasNewCar(transportAssignment) ? (
                    <IncidentModal transportAssignment={transportAssignment} />
                ) : (
                    <div>&nbsp;</div>
                )}
                <ConfirmationButton
                    isUnconfirmed={isAssignmentUnconfirmed(transportAssignment)}
                    isCapacity={isCapacityOrder(transportAssignment)}
                    isLoading={isLoading}
                    isButtonDisabled={validation.isButtonDisabled || isFormDisabled}
                    onConfirm={onConfirmTransportAssignment}
                />
            </div>
        </>
    );
};

const isAssignmentUnconfirmed = (transportAssignment: TransportAssignment): boolean => {
    const allVehicles = mapToVehiclesInExecution(transportAssignment);
    const isConfirmed = getIsConfirmed(transportAssignment);

    return (
        !isConfirmed &&
        !hasVehiclesInStatus(allVehicles, ExecutionStatus.LOADED) &&
        !hasVehiclesInStatus(allVehicles, ExecutionStatus.UNLOADED)
    );
};
